import { axiosFetch as authFetch } from './axiosFetch';
import { getStaffPermissions } from './api';
import { CREDENTIALS } from '../constants';
import { messageFromRevampErrorBody } from '../utils/revampApiErrorMessage';

/**
 * Maghil Menu rich media (maghil-backend-v3, docs/plans/maghil-menu-rich-media-tables-and-apis.md).
 *   P1  GET    /rich-media/locations/{locationId}
 *   P2  POST   /rich-media/upload?locationId&div[&itemId]   multipart field "media" (not used; kept as a fallback)
 *   P2a POST   /rich-media/upload-url      { locationId, div, itemId?, contentType, size } → signed PUT URL
 *   P2b POST   /rich-media/upload-complete { locationId, div, itemId?, uploadPath, originalName }
 *   P3  DELETE /rich-media/remove?locationId&mediaId
 *   P4  POST   /rich-media/sync?locationId
 *   P5  POST   /rich-media/arrange   { locationId, entityType: LOCATION|ITEM, itemId?, media: [{ mediaId, div }] }
 */
const REVAMP_BASE = (process.env.REACT_APP_API_ENDPOINT_REVAMP || '').replace(/\/$/, '');
const RICH_MEDIA_BASE = `${REVAMP_BASE}/api/v1/rich-media`;

export interface RichMediaFile {
  mediaId: string;
  div: string;
  fileName: string;
  storagePath: string;
  mimeType: string;
  modifiedTime: string;
}

/** Item with its media. Category grouping and order come from the Product Catalog categories API. */
export interface RichMediaItem {
  itemId: string;
  itemName: string;
  media: RichMediaFile[];
}

export interface LocationRichMediaResponse {
  locationId: string;
  baseUrl: string;
  location: { media: RichMediaFile[] };
  totalItems: number;
  items: RichMediaItem[];
}

export interface UploadRichMediaResponse extends RichMediaFile {
  itemId: string | null;
  replaced: boolean;
}

export interface RemoveRichMediaResponse {
  mediaId: string;
  itemId: string | null;
  div: string;
  deleted: boolean;
}

export interface MediaSyncResponse {
  locationId: string;
  eventName: string;
  publishedAt: string;
  published: boolean;
}

export class RichMediaApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = 'RichMediaApiError';
  }
}

function storedCredentials(): any {
  try {
    const raw = localStorage.getItem(CREDENTIALS);
    if (!raw) return null;
    const stored = JSON.parse(raw);
    return stored?.credentials ?? stored;
  } catch {
    return null;
  }
}

function getRevampToken(): string | null {
  try {
    const cred = storedCredentials();
    if (!cred) return null;
    const token =
      cred?.accessToken ??
      cred?.access_token ??
      cred?.token ??
      cred?.idToken ??
      cred?.id_token ??
      cred?.jwtToken ??
      cred?.jwt;
    return token && typeof token === 'string' && token.trim() ? token.trim() : null;
  } catch {
    return null;
  }
}

function authHeaders(json: boolean): Record<string, string> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (json) headers['Content-Type'] = 'application/json';
  const token = getRevampToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

async function request<T>(url: string, init: RequestInit & { onUploadProgress?: (e: any) => void }, fallback: string): Promise<T> {
  let res: Response;
  try {
    res = await authFetch(url, init);
  } catch {
    throw new RichMediaApiError('Could not reach the server. Check your connection and try again.', 0);
  }
  let data: unknown = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  if (!res.ok) {
    throw new RichMediaApiError(messageFromRevampErrorBody(data, fallback), res.status);
  }
  return data as T;
}

/** P1 – outlet media plus every item with its media. */
export function getLocationRichMedia(locationId: string): Promise<LocationRichMediaResponse> {
  return request<LocationRichMediaResponse>(
    `${RICH_MEDIA_BASE}/locations/${encodeURIComponent(locationId)}`,
    { method: 'GET', headers: authHeaders(false) },
    'Failed to load media',
  );
}

interface RichMediaUploadUrlResponse {
  uploadPath: string;
  uploadUrl: string;
  method: 'PUT';
  headers: Record<string, string>;
  expiresAt: string;
}

/**
 * Type the backend signs the URL for. The backend re-detects the real type from the file's bytes on
 * upload-complete; this only has to be one of the allowed types.
 */
function uploadContentType(file: File): string {
  if (file.type === 'image/jpg') return 'image/jpeg';
  if (file.type) return file.type;
  const ext = file.name.split('.').pop()?.toLowerCase();
  const byExt: Record<string, string> = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp', mp4: 'video/mp4' };
  return (ext && byExt[ext]) || 'application/octet-stream';
}

function webViewXhr(): {
  xhr: XMLHttpRequest;
  open: XMLHttpRequest['open'];
  setRequestHeader: XMLHttpRequest['setRequestHeader'];
  send: XMLHttpRequest['send'];
} {
  const original = (window as any).CapacitorWebXMLHttpRequest;
  if (original?.constructor && original.open && original.send && original.setRequestHeader) {
    return {
      xhr: new original.constructor(),
      open: original.open,
      setRequestHeader: original.setRequestHeader,
      send: original.send,
    };
  }
  const xhr = new XMLHttpRequest();
  return { xhr, open: xhr.open, setRequestHeader: xhr.setRequestHeader, send: xhr.send };
}

/** PUT the file with plain XHR; resolves with the HTTP status (0 = network or CORS failure). */
function xhrPut(url: string, headers: Record<string, string>, file: File, onProgress?: (percent: number) => void): Promise<number> {
  return new Promise((resolve) => {
    const { xhr, open, setRequestHeader, send } = webViewXhr();
    xhr.upload.addEventListener('progress', (event) => {
      if (!onProgress || !event.lengthComputable || !event.total) return;
      // Hold below 100 until upload-complete answers.
      onProgress(Math.min(95, Math.max(1, Math.floor((event.loaded / event.total) * 100))));
    });
    xhr.addEventListener('load', () => resolve(xhr.status));
    xhr.addEventListener('error', () => resolve(0));
    xhr.addEventListener('abort', () => resolve(0));
    xhr.addEventListener('timeout', () => resolve(0));
    open.call(xhr, 'PUT', url, true);
    Object.entries(headers).forEach(([name, value]) => setRequestHeader.call(xhr, name, value));
    send.call(xhr, file);
  });
}

/** PUT the raw file to the signed URL. No Authorization header: the signature is the permission. */
async function putToSignedUrl(
  target: RichMediaUploadUrlResponse,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<void> {
  const status = await xhrPut(target.uploadUrl, target.headers, file, onProgress);
  if (status === 0) {
    // Network failure or a CORS rejection (the browser hides the details of both).
    throw new RichMediaApiError('Could not upload the file. Check your connection and try again.', 0);
  }
  if (status >= 200 && status < 300) return;
  if (status === 400 || status === 403) {
    // Signature mismatch, expired URL or a body over the size limit.
    throw new RichMediaApiError('The upload was rejected. Please try again.', status);
  }
  throw new RichMediaApiError('Failed to upload file', status);
}

/**
 * P2 – put a file into a div. Without itemId the file is outlet media.
 * Direct upload: P2a gets a signed URL, the file goes straight from the browser to the bucket,
 * then P2b checks it and puts it into the div. The response is the same as the multipart P2.
 */
export async function uploadRichMedia(params: {
  locationId: string;
  div: string;
  itemId?: string | null;
  file: File;
  onProgress?: (percent: number) => void;
}): Promise<UploadRichMediaResponse> {
  const slot = {
    locationId: params.locationId,
    div: params.div,
    ...(params.itemId ? { itemId: params.itemId } : {}),
  };

  params.onProgress?.(1);
  const target = await request<RichMediaUploadUrlResponse>(
    `${RICH_MEDIA_BASE}/upload-url`,
    {
      method: 'POST',
      headers: authHeaders(true),
      body: JSON.stringify({ ...slot, contentType: uploadContentType(params.file), size: params.file.size }),
    },
    'Failed to upload file',
  );

  await putToSignedUrl(target, params.file, params.onProgress);

  return request<UploadRichMediaResponse>(
    `${RICH_MEDIA_BASE}/upload-complete`,
    {
      method: 'POST',
      headers: authHeaders(true),
      body: JSON.stringify({ ...slot, uploadPath: target.uploadPath, originalName: params.file.name }),
    },
    'Failed to upload file',
  );
}

/** P3 – remove a file. */
export function removeRichMedia(locationId: string, mediaId: string): Promise<RemoveRichMediaResponse> {
  const query = new URLSearchParams({ locationId, mediaId });
  return request<RemoveRichMediaResponse>(
    `${RICH_MEDIA_BASE}/remove?${query.toString()}`,
    { method: 'DELETE', headers: authHeaders(false) },
    'Failed to remove file',
  );
}

/** P4 – publish the media sync event for the location's Maghil Menu devices. */
export function syncRichMedia(locationId: string): Promise<MediaSyncResponse> {
  return request<MediaSyncResponse>(
    `${RICH_MEDIA_BASE}/sync?locationId=${encodeURIComponent(locationId)}`,
    { method: 'POST', headers: authHeaders(true) },
    'Sync failed. Try again.',
  );
}

export type RichMediaEntityType = 'LOCATION' | 'ITEM';

/** A current file and the div it should be in. */
export interface RichMediaPlacement {
  mediaId: string;
  div: string;
}

export interface ArrangeRichMediaResponse {
  locationId: string;
  entityType: RichMediaEntityType;
  itemId: string | null;
  moved: number;
  media: RichMediaPlacement[];
}

/**
 * P5 – move files of the outlet (LOCATION) or one item (ITEM) to other divs in one call. Only the listed
 * files move; a file going into a filled div needs that div's file listed with its new div too.
 */
export function arrangeRichMedia(params: {
  locationId: string;
  entityType: RichMediaEntityType;
  itemId?: string | null;
  media: RichMediaPlacement[];
}): Promise<ArrangeRichMediaResponse> {
  return request<ArrangeRichMediaResponse>(
    `${RICH_MEDIA_BASE}/arrange`,
    {
      method: 'POST',
      headers: authHeaders(true),
      body: JSON.stringify({
        locationId: params.locationId,
        entityType: params.entityType,
        ...(params.entityType === 'ITEM' && params.itemId ? { itemId: params.itemId } : {}),
        media: params.media,
      }),
    },
    'Could not change the positions. Try again.',
  );
}

/**
 * A Product Catalog category as Media Management needs it (grouping and ordering the Items list).
 * Read straight from the categories API so this module does not depend on the Product Catalog code.
 */
export interface RichMediaCategory {
  id: string;
  name: string;
  parentCategoryId: string | null;
  sortPosition: number;
  itemIds: string[];
  /** itemId -> position of the item inside this category. */
  itemSortOrders: Record<string, number>;
}

interface ApiCategoryRow {
  category_id?: string;
  name?: string;
  sortOrder?: number;
  parentCategoryId?: string | null;
  items?: Array<{ itemID?: string; sortOrder?: number }>;
}

function toRichMediaCategory(api: ApiCategoryRow): RichMediaCategory {
  const items = Array.isArray(api.items) ? api.items : [];
  return {
    id: api.category_id ?? '',
    name: api.name ?? '',
    parentCategoryId: api.parentCategoryId || null,
    sortPosition: api.sortOrder ?? 0,
    itemIds: items.map((i) => i.itemID ?? '').filter(Boolean),
    itemSortOrders: items.reduce<Record<string, number>>((acc, i) => {
      if (i.itemID && i.sortOrder != null) acc[i.itemID] = i.sortOrder;
      return acc;
    }, {}),
  };
}

/** Categories of the location (parents and subcategories) with their items. */
export async function getRichMediaCategories(locationId: string): Promise<RichMediaCategory[]> {
  const data = await request<{ parentcatagory?: ApiCategoryRow[]; subcategory?: ApiCategoryRow[] }>(
    `${REVAMP_BASE}/api/v1/category/get-categories?locationId=${encodeURIComponent(locationId)}`,
    { method: 'GET', headers: authHeaders(false) },
    'Failed to load categories',
  );
  const parents = Array.isArray(data?.parentcatagory) ? data.parentcatagory : [];
  const subs = Array.isArray(data?.subcategory) ? data.subcategory : [];
  return [...parents, ...subs].map(toRichMediaCategory).filter((c) => c.id);
}

export function richMediaUrl(baseUrl: string, file: Pick<RichMediaFile, 'storagePath' | 'modifiedTime'>): string {
  const base = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
  const url = `${base}${file.storagePath}`;
  if (!file.modifiedTime) return url;
  return `${url}${url.includes('?') ? '&' : '?'}v=${encodeURIComponent(file.modifiedTime)}`;
}

/* ---------- Kiosk Media permissions ---------- */

/**
 * KIOSK MEDIA section of the employee Roles & Functions editor (sections come from GET /api-info).
 * Edit Media implies View Media. Magil_Admin (Brisque Admin) always has both.
 */
export const KIOSK_MEDIA_MODULE = 'KIOSK MEDIA';
export const VIEW_MEDIA = 'View Media';
export const EDIT_MEDIA = 'Edit Media';
const ADMIN_ROLE = 'Magil_Admin';

export interface KioskMediaAccess {
  canView: boolean;
  canEdit: boolean;
}

export const NO_KIOSK_MEDIA_ACCESS: KioskMediaAccess = { canView: false, canEdit: false };

const norm = (value: unknown) => String(value ?? '').trim().toLowerCase();

/** Reads KIOSK MEDIA from the staff permissions response: [{ module, funtions: [name] }] (the backend spells it "funtions"). */
export function kioskMediaAccessOf(res: any): KioskMediaAccess {
  const list = [res, res?.metaDataInfo?.data, res?.data].find(Array.isArray) ?? [];
  const mod = list.find((m: any) => norm(m?.module) === norm(KIOSK_MEDIA_MODULE));
  const fns: unknown[] = mod?.funtions ?? mod?.functions ?? mod?.functionality ?? [];
  const names = fns.map((f: any) => norm(typeof f === 'string' ? f : f?.name ?? f?.displayName));
  const canEdit = names.includes(norm(EDIT_MEDIA));
  return { canEdit, canView: canEdit || names.includes(norm(VIEW_MEDIA)) };
}

/**
 * The signed-in employee's Kiosk Media permissions, read fresh from Keycloak on every call
 * (POST /merchants/staff/permissions), so a changed permission applies without logging in again.
 */
export async function getKioskMediaAccess(): Promise<KioskMediaAccess> {
  let stored: any = null;
  try {
    stored = JSON.parse(localStorage.getItem(CREDENTIALS) || 'null');
  } catch {
    stored = null;
  }
  const cred = storedCredentials() ?? {};
  const pick = (...keys: string[]) => keys.map((k) => stored?.[k] ?? cred?.[k]).find((v) => v != null && v !== '');

  if (pick('role', 'designation') === ADMIN_ROLE) return { canView: true, canEdit: true };
  const staffId = pick('staff_id', 'staffId', 'id');
  const merchantId = pick('merchantId');
  if (!staffId || !merchantId) return NO_KIOSK_MEDIA_ACCESS;

  const res = await getStaffPermissions({ staffId: String(staffId), merchantId: String(merchantId), token: getRevampToken() });
  return kioskMediaAccessOf(res);
}
